/**
 * TwoFactorGuardProvider - Blocks workspace access for non-compliant users
 *
 * sl-qsjm.5: Original implementation with full-screen overlay.
 * sl-d6mh.4.3: Extended to differentiate between grace period and hard lock.
 *
 * During grace period: children render normally (TopTip banner handles the warning).
 * After grace period: hard lock overlay with inline TOTP setup.
 */
import { notify } from '@affine/component';
import { TotpSetupDialog } from '@affine/core/components/affine/auth/totp-setup-dialog';
import { useWorkspaceTwoFactorPolicy } from '@affine/core/hooks/use-workspace-two-factor-policy';
import { GraphQLService } from '@affine/core/modules/cloud/services/graphql';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { isWorkspaceTwoFactorCompliantQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { LockIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export function TwoFactorGuardProvider({ children }: PropsWithChildren) {
  const workspace = useService(WorkspaceService).workspace;
  const graphqlService = useService(GraphQLService);
  const t = useI18n();

  const workspaceDialogService = useService(WorkspaceDialogService);
  const [isCompliant, setIsCompliant] = useState<boolean | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const toastShownRef = useRef(false);

  const twoFactorPolicy = useWorkspaceTwoFactorPolicy(
    workspace.id,
    workspace.flavour
  );

  const checkCompliance = useCallback(async () => {
    // Only check cloud workspaces
    if (workspace.flavour === 'local') {
      setIsCompliant(true);
      return;
    }

    try {
      const result = await graphqlService.gql({
        query: isWorkspaceTwoFactorCompliantQuery,
        variables: { workspaceId: workspace.id },
      });
      setIsCompliant(result.isWorkspaceTwoFactorCompliant);
    } catch {
      // If we can't check, don't block — fail open
      setIsCompliant(true);
    }
  }, [graphqlService, workspace.id, workspace.flavour]);

  useEffect(() => {
    void checkCompliance();
  }, [checkCompliance]);

  // Re-check compliance periodically while non-compliant (user may set up 2FA)
  useEffect(() => {
    if (isCompliant !== false) return;
    const interval = setInterval(() => {
      void checkCompliance();
    }, 5000);
    return () => clearInterval(interval);
  }, [checkCompliance, isCompliant]);

  // sl-0aw1.2.1: Toast notification during grace period for non-compliant users
  useEffect(() => {
    if (
      toastShownRef.current ||
      isCompliant !== false ||
      !twoFactorPolicy.requireTOTP ||
      !twoFactorPolicy.gracePeriodActive ||
      twoFactorPolicy.gracePeriodRemainingDays === null
    ) {
      return;
    }

    toastShownRef.current = true;
    const days = twoFactorPolicy.gracePeriodRemainingDays;
    notify.warning({
      title: t['com.streamlain.two-factor-guard.grace-period-toast.title'](),
      message: t['com.streamlain.two-factor-guard.grace-period-toast.message']({
        days: String(days),
      }),
      actions: [
        {
          key: 'setup',
          label:
            t['com.streamlain.two-factor-guard.grace-period-toast.action'](),
          onClick: () =>
            workspaceDialogService.open('setting', {
              activeTab: 'account',
            }),
        },
      ],
    });
  }, [
    isCompliant,
    twoFactorPolicy.requireTOTP,
    twoFactorPolicy.gracePeriodActive,
    twoFactorPolicy.gracePeriodRemainingDays,
    t,
    workspaceDialogService,
  ]);

  const handleSetupComplete = useCallback(() => {
    setSetupOpen(false);
    // Trigger immediate re-check
    void checkCompliance();
  }, [checkCompliance]);

  // Still loading
  if (isCompliant === null) {
    return <>{children}</>;
  }

  // User is compliant — render workspace normally
  if (isCompliant) {
    return <>{children}</>;
  }

  // User is NOT compliant but grace period is still active — render normally
  // (TopTip banner from sl-d6mh.4.2 handles the warning)
  if (twoFactorPolicy.requireTOTP && twoFactorPolicy.gracePeriodActive) {
    return <>{children}</>;
  }

  // User is NOT compliant and grace period expired (or no grace period) — hard lock
  return (
    <>
      {children}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <div
          style={{
            background: 'var(--streamlain-v2-layer-background-primary)',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '440px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
        >
          <LockIcon
            style={{
              fontSize: '48px',
              marginBottom: '16px',
              color: 'var(--streamlain-v2-brand-primary)',
            }}
          />
          <h2
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: 'var(--streamlain-v2-text-primary)',
              margin: '0 0 8px 0',
            }}
          >
            {t['com.streamlain.two-factor-guard.title']()}
          </h2>
          <p
            style={{
              fontSize: '14px',
              color: 'var(--streamlain-v2-text-secondary)',
              margin: '0 0 24px 0',
              lineHeight: '1.5',
            }}
          >
            {t['com.streamlain.two-factor-guard.description']()}
          </p>
          <button
            onClick={() => setSetupOpen(true)}
            style={{
              padding: '10px 24px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--streamlain-v2-brand-primary)',
              color: 'var(--streamlain-v2-layer-white)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t['com.streamlain.two-factor-guard.setup-button']()}
          </button>
        </div>
      </div>
      <TotpSetupDialog
        open={setupOpen}
        onOpenChange={setSetupOpen}
        onComplete={handleSetupComplete}
      />
    </>
  );
}
